/**
 * China's flagship deep dive: Chongli (/chongli/). The travel guide and FAQ pages were
 * merged into it (data/merged-pages.json): their sections follow the deep dive and their
 * questions follow the page FAQ below. Rendered by src/pages/flagship/*.tsx; the route slug comes
 * from SiteConfig.routes.flagship. Shape: FlagshipData in src/data/flagship.ts.
 * Copy and sources: ./guides/deepDive.ts and sites/cn/CONTENT_SOURCES.md.
 */
import type { FlagshipData } from '@/data/flagship';
import type { CnLang } from './guides/types';
import { CHONGLI_DEEP_DIVE, CHONGLI_FAQ, CHONGLI_PAGE, CHONGLI_TRAVEL_GUIDE } from './guides/deepDive';

/** The deep-dive copy writes links with a trailing slash; the flagship pages add their own. */
const mergeFaq = (own: { q: string; a: string }[], extra: { q: string; a: string }[]) => [
  ...own,
  ...extra.filter((item) => !own.some((o) => o.q === item.q)),
];

const trim = (to: string) => (to.length > 1 ? to.replace(/\/+$/, '') : to);

export const FLAGSHIP: FlagshipData = {
  resortSlug: 'wanlong',
  name: { en: 'Chongli', zh: '崇礼', ko: '충리', ja: '崇礼' },
  resortName: {
    en: 'Chongli ski resorts (Zhangjiakou)',
    zh: '崇礼滑雪场（张家口）',
    ko: '충리 스키장(장자커우)',
    ja: '崇礼のスキー場（張家口）',
  },
  badge: { en: 'Hebei · Chongli', zh: '河北 崇礼', ko: '허베이 · 충리', ja: '河北省 崇礼' },
  address: 'Chongli District, Zhangjiakou, Hebei, China',
  region: 'Hebei',
  latitude: 40.97,
  longitude: 115.45,
  faqRelated: {
    resortGuide: { en: 'Chongli guide', zh: '崇礼指南', ko: '충리 가이드', ja: '崇礼ガイド' },
    travelGuide: { en: 'Chongli travel guide', zh: '崇礼旅行指南', ko: '충리 여행 가이드', ja: '崇礼 旅行ガイド' },
    countryFaq: { en: 'Skiing in China FAQ', zh: '中国滑雪常见问题', ko: '중국 스키 FAQ', ja: '中国スキーのよくある質問' },
  },
  main: Object.fromEntries(
    Object.entries(CHONGLI_PAGE).map(([lang, copy]) => [
      lang,
      {
        ...copy,
        links: copy.links.map((link) => ({ ...link, to: trim(link.to) })),
        faq: mergeFaq(copy.faq, CHONGLI_FAQ[lang as CnLang].faq),
      },
    ]),
  ) as FlagshipData['main'],
  deepDive: Object.fromEntries(
    Object.entries(CHONGLI_DEEP_DIVE).map(([lang, dive]) => [
      lang,
      {
        ...dive,
        sections: [
          ...dive.sections,
          ...CHONGLI_TRAVEL_GUIDE[lang as CnLang].sections.map((section) => ({
            id: `guide-${section.icon}`,
            heading: section.heading,
            body: section.body.join(' '),
          })),
        ],
      },
    ]),
  ) as FlagshipData['deepDive'],
  // The travel-guide and FAQ pages are merged into /chongli/; these stay as the shape FlagshipData expects.
  guide: Object.fromEntries(
    Object.entries(CHONGLI_TRAVEL_GUIDE).map(([lang, copy]) => [
      lang,
      { ...copy, links: copy.links.map((link) => ({ ...link, to: trim(link.to) })) },
    ]),
  ) as FlagshipData['guide'],
  faq: CHONGLI_FAQ,
};
